// InvoicePreviewItems.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import { formatPrice } from 'utilities/Currency';

// Local
import {
  INVOICE_ITEM_TYPE_KEY,
  readInvoiceField,
} from 'nodetypes/invoice/view/invoiceGeneralInfoModel';
import {
  isInvoiceColumnClientVisible,
  normalizeInvoiceClientVisibleColumns,
} from 'nodetypes/invoice/view/invoiceItemsDisplayPrefs';
import {
  DOCUMENT_PAYMENT_TYPE_KEY,
} from 'nodetypes/documents/payments/documentPaymentKeys';
import {
  computeInvoiceItemsRollups,
  getInvoiceItemRowModel,
  readInvoiceItemField,
} from 'nodetypes/invoice/view/invoiceItemsModel';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderItems.css';
import './InvoiceItems.css';
import './InvoicePreviewItems.css';

function cleanString(value) {
  return String(value || '').trim();
}

function formatMoneyCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="purchase-order-items-muted">—</span>;
  }
  return formatPrice(value);
}

function formatMarkupCell(markupAmount, markupPercent) {
  const amountText = markupAmount == null ? '' : formatPrice(markupAmount);
  const percentText = markupPercent == null ? '' : `${markupPercent}%`;
  if (!amountText && !percentText) {
    return <span className="purchase-order-items-muted">—</span>;
  }
  if (amountText && percentText) return `${amountText} (${percentText})`;
  return amountText || percentText;
}

function formatPlainCell(value) {
  const cleaned = cleanString(value);
  if (!cleaned) return <span className="purchase-order-items-muted">—</span>;
  return cleaned;
}

const ItemThumb = React.memo(({ itemKey, coverUrl }) => {
  const [src, setSrc] = useState(coverUrl || '');

  useEffect(() => {
    let cancelled = false;
    const raw = cleanString(coverUrl);
    if (!raw) {
      setSrc('');
      return undefined;
    }
    if (
      raw.startsWith('blob:')
      || raw.startsWith('data:')
      || raw.startsWith('http://')
      || raw.startsWith('https://')
      || raw.startsWith('/')
    ) {
      setSrc(raw);
      return undefined;
    }
    (async () => {
      try {
        const signed = await resolveImageUrl({ nodeKey: itemKey || null, url: raw });
        if (!cancelled) setSrc(signed || raw);
      } catch {
        if (!cancelled) setSrc(raw);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coverUrl, itemKey]);

  if (!src) return null;

  return (
    <div className="purchase-order-items-thumb" aria-hidden="true">
      <img src={src} alt="" draggable="false" loading="lazy" />
    </div>
  );
});

ItemThumb.displayName = 'ItemThumb';

/**
 * InvoicePreviewItems
 *
 * Read-only client-facing items table for Invoice Preview.
 * Columns follow `clientVisibleColumns` (not local Manage columns prefs).
 *
 * @param {object} node - Invoice node.
 * @param {object|null} [nodeType]
 * @param {object[]} [neighborNodes]
 * @returns {ReactNode}
 */
const InvoicePreviewItems = React.memo(({
  node,
  nodeType = null,
  neighborNodes = [],
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();

  // ----- MEMOS ----- //

  const baseKey = cleanString(activeBase?.key || node?.baseKey);
  const itemType = useMemo(() => getType(INVOICE_ITEM_TYPE_KEY), [getType]);

  const clientVisibleColumns = useMemo(
    () => normalizeInvoiceClientVisibleColumns(
      readInvoiceField(node, 'clientVisibleColumns', nodeType),
    ),
    [node, nodeType],
  );

  const show = useMemo(() => {
    const visible = (key) => isInvoiceColumnClientVisible(clientVisibleColumns, key);
    return {
      sku: visible('sku'),
      itemType: visible('itemType'),
      category: visible('category'),
      status: visible('status'),
      manufacturer: visible('manufacturer'),
      vendor: visible('vendor'),
      cost: visible('cost'),
      msrp: visible('msrp'),
      markup: visible('markup'),
      margin: visible('margin'),
      room: visible('room'),
      color: visible('color'),
      dimensions: visible('dimensions'),
      shipping: visible('shipping'),
      shippingMarkup: visible('shippingMarkup'),
      taxable: visible('taxable'),
      website: visible('website'),
    };
  }, [clientVisibleColumns]);

  const {
    options: vendorOptions,
  } = useNodelistTargetOptions({
    targetType: 'VENDOR',
    baseKey,
    enabled: Boolean(baseKey && show.vendor),
  });
  const {
    options: roomOptions,
  } = useNodelistTargetOptions({
    targetType: 'SPACE',
    baseKey,
    enabled: Boolean(baseKey && show.room),
  });

  const vendorLabelByKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(vendorOptions) ? vendorOptions : []).forEach((option) => {
      const value = cleanString(option?.value);
      if (value) map.set(value, cleanString(option?.label) || value);
    });
    return map;
  }, [vendorOptions]);

  const roomLabelByKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(roomOptions) ? roomOptions : []).forEach((option) => {
      const value = cleanString(option?.value);
      if (value) map.set(value, cleanString(option?.label) || value);
    });
    return map;
  }, [roomOptions]);

  const items = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === INVOICE_ITEM_TYPE_KEY)
      .sort((left, right) => {
        const leftOrder = Number(readInvoiceItemField(left, 'sortOrder'));
        const rightOrder = Number(readInvoiceItemField(right, 'sortOrder'));
        if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return cleanString(left?.title).localeCompare(cleanString(right?.title));
      })
  ), [neighborNodes]);

  const rows = useMemo(
    () => items.map((item) => ({
      item,
      model: getInvoiceItemRowModel(item, itemType, language),
    })),
    [itemType, items, language],
  );

  const payments = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === DOCUMENT_PAYMENT_TYPE_KEY)
  ), [neighborNodes]);

  const rollups = useMemo(
    () => computeInvoiceItemsRollups(items, node, nodeType, payments),
    [items, node, nodeType, payments],
  );

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div className="invoice-preview-items-container">
      <div className="purchase-order-items-title">
        {`Items (${rows.length})`}
      </div>

      {rows.length === 0 ? (
        <div className="purchase-order-items-empty">
          No items yet.
        </div>
      ) : (
        <div className="purchase-order-items-scroll">
          <table className="purchase-order-items-table invoice-items-table invoice-preview-items-table">
            <thead>
              <tr>
                <th className="purchase-order-items-col-line" scope="col">#</th>
                <th className="purchase-order-items-col-item">Item</th>
                {show.sku ? <th className="invoice-items-col-sku">SKU</th> : null}
                {show.itemType ? <th>Type</th> : null}
                {show.category ? <th>Category</th> : null}
                {show.status ? <th className="purchase-order-items-col-status">Status</th> : null}
                {show.manufacturer ? <th>Manufacturer</th> : null}
                {show.vendor ? <th>Vendor</th> : null}
                <th className="purchase-order-items-col-qty">Qty</th>
                <th className="purchase-order-items-col-unit">Unit</th>
                {show.cost ? <th className="invoice-items-col-money">Cost</th> : null}
                {show.msrp ? <th className="invoice-items-col-money">MSRP</th> : null}
                {show.markup ? <th className="invoice-items-col-markup">Markup</th> : null}
                {show.margin ? <th className="invoice-items-col-money">Margin</th> : null}
                <th className="purchase-order-items-col-unit-price">Unit Price</th>
                {show.room ? <th>Room</th> : null}
                {show.color ? <th>Color</th> : null}
                {show.dimensions ? <th>Dimensions</th> : null}
                {show.shipping ? <th className="invoice-items-col-money">Shipping</th> : null}
                {show.shippingMarkup ? <th>Ship Markup</th> : null}
                {show.taxable ? <th>Taxable</th> : null}
                {show.website ? <th>Website</th> : null}
                <th className="purchase-order-items-col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ model }, index) => (
                <tr key={model.key || index}>
                  <td className="purchase-order-items-col-line">{index + 1}</td>
                  <td className="purchase-order-items-col-item">
                    <div className="purchase-order-items-item-cell">
                      <ItemThumb itemKey={model.key} coverUrl={model.coverUrl} />
                      <div className="purchase-order-items-item-text">
                        <div className="invoice-preview-items-title">
                          {model.title || 'Untitled'}
                        </div>
                        {model.description ? (
                          <div className="invoice-preview-items-description">
                            {model.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  {show.sku ? <td>{formatPlainCell(model.sku)}</td> : null}
                  {show.itemType ? <td>{formatPlainCell(model.itemTypeLabel || model.itemType)}</td> : null}
                  {show.category ? <td>{formatPlainCell(model.categoryLabel || model.category)}</td> : null}
                  {show.status ? <td>{formatPlainCell(model.status)}</td> : null}
                  {show.manufacturer ? <td>{formatPlainCell(model.manufacturer)}</td> : null}
                  {show.vendor ? (
                    <td>
                      {formatPlainCell(
                        vendorLabelByKey.get(model.vendorKey) || model.vendorKey,
                      )}
                    </td>
                  ) : null}
                  <td className="purchase-order-items-col-qty">
                    {model.quantity == null
                      ? <span className="purchase-order-items-muted">—</span>
                      : model.quantity}
                  </td>
                  <td className="purchase-order-items-col-unit">
                    {formatPlainCell(model.unitLabel || model.unit)}
                  </td>
                  {show.cost ? (
                    <td className="invoice-items-col-money">{formatMoneyCell(model.cost)}</td>
                  ) : null}
                  {show.msrp ? (
                    <td className="invoice-items-col-money">{formatMoneyCell(model.msrp)}</td>
                  ) : null}
                  {show.markup ? (
                    <td className="invoice-items-col-markup">
                      {formatMarkupCell(model.markupAmount, model.markupPercent)}
                    </td>
                  ) : null}
                  {show.margin ? (
                    <td className="invoice-items-col-money">
                      {model.marginPercent == null
                        ? <span className="purchase-order-items-muted">—</span>
                        : `${model.marginPercent}%`}
                    </td>
                  ) : null}
                  <td className="purchase-order-items-col-unit-price">
                    {formatMoneyCell(model.unitPrice)}
                  </td>
                  {show.room ? (
                    <td>
                      {formatPlainCell(
                        roomLabelByKey.get(model.roomKey) || model.roomKey,
                      )}
                    </td>
                  ) : null}
                  {show.color ? <td>{formatPlainCell(model.color)}</td> : null}
                  {show.dimensions ? <td>{formatPlainCell(model.dimensions)}</td> : null}
                  {show.shipping ? (
                    <td className="invoice-items-col-money">{formatMoneyCell(model.shipping)}</td>
                  ) : null}
                  {show.shippingMarkup ? (
                    <td>
                      {model.shippingMarkupPercent == null
                        ? <span className="purchase-order-items-muted">—</span>
                        : `${model.shippingMarkupPercent}%`}
                    </td>
                  ) : null}
                  {show.taxable ? <td>{model.taxable ? 'Yes' : 'No'}</td> : null}
                  {show.website ? (
                    <td>
                      {model.website ? (
                        <a
                          className="invoice-preview-items-link"
                          href={model.website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {model.website}
                        </a>
                      ) : (
                        <span className="purchase-order-items-muted">—</span>
                      )}
                    </td>
                  ) : null}
                  <td className="purchase-order-items-col-total">
                    {formatMoneyCell(model.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="purchase-order-items-footer">
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Subtotal</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.subTotal)}
          </span>
        </div>
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Tax</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.tax)}
          </span>
        </div>
        <hr className="purchase-order-items-footer-rule" />
        <div className="purchase-order-items-footer-row is-emphasis">
          <span className="purchase-order-items-footer-label">Total</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.total)}
          </span>
        </div>
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Amount Paid</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.amountPaid)}
          </span>
        </div>
        <div className="purchase-order-items-footer-row is-emphasis">
          <span className="purchase-order-items-footer-label">Balance Due</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.balanceDue)}
          </span>
        </div>
      </div>
    </div>
  );
});

// Dev Tools
InvoicePreviewItems.displayName = 'InvoicePreviewItems';

export default InvoicePreviewItems;
